import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState, useEffect } from 'react';

import {
    EikaMessageEvent,
    GtpPushProvisionResult,
    GtpTokenStatusResult,
    parseCallback,
    useNativeEventListener,
} from '@eika-infrastruktur/mobilbank-lib';

import { Logger } from '@/domain/app';

import { DigitalWalletService, GooglePayData } from '@domain/digital-wallet';
import { CreditCardTokenStatus, DigitalWalletTokenStatus } from '@generated-types';
import useText from '@hooks/use-text';

import { GpayCardStatus } from '../services/DigitalWalletService';

interface GoogleTokenInformation {
    currentStatus: GpayCardStatus;
    tokenizationError?: string;
}

type GtpWebAction =
    | 'GTP_TOKEN_STATUS_RESULT'
    | 'GTP_TOKEN_STATUS_ERROR'
    | 'GTP_PUSH_PROVISION_RESULT'
    | 'GTP_PUSH_PROVISION_ERROR';

export const useGoogleDigitalCardTokenStatus = (data: GooglePayData): GoogleTokenInformation => {
    const text = useText();
    const queryClient = useQueryClient();

    const [currentStatus, setCurrentStatus] = useState<GpayCardStatus>('INIT');
    const [tokenizationError, setTokenizationError] = useState<string>();

    useEffect(() => {
        Logger.warn('wallet', 'effect', 'useGoogleDigitalCardTokenStatus', {
            data,
            currentStatus,
            tokenizationError,
        });

        if (data.source === 'te') {
            if (!data.digitalCard) {
                return;
            }
            if (data.digitalCard.tokenUniqueReference && data.digitalCard.status === DigitalWalletTokenStatus.ACTIVE) {
                DigitalWalletService.checkNativeGooglePayTokenStatus(data.digitalCard.tokenUniqueReference);
            } else {
                setCurrentStatus('UNREGISTERED');
            }
            return;
        }
        if (data.elibigleCard.tokens.length === 0) {
            setCurrentStatus('UNREGISTERED');
            return;
        }
        const activeToken = data.elibigleCard.tokens.find((token) => token.status === CreditCardTokenStatus.ACTIVE);

        if (activeToken?.uniqueReferenceId) {
            DigitalWalletService.checkNativeGooglePayTokenStatus(activeToken.uniqueReferenceId);
        } else {
            setCurrentStatus('UNREGISTERED');
        }
        // NOTE: only run when data changed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const gpayReturnHandle = useCallback(
        (event: EikaMessageEvent<GtpWebAction>) => {
            const data = parseCallback(event.data);

            if (data) {
                Logger.warn('wallet', 'google wallet control message', 'useGoogleDigitalCardTokenStatus', { data });
                switch (data.action) {
                    case 'GTP_TOKEN_STATUS_RESULT': {
                        const gtpTokenizationResult = data.payload as GtpTokenStatusResult;

                        switch (gtpTokenizationResult.status) {
                            case 'SUSPENDED':
                                break;

                            case 'PENDING': {
                                setCurrentStatus('REGISTERED');
                                setTokenizationError(text.googleWalletGetGoingTokenizationErrorAlreadyExists);
                                break;
                            }

                            case 'ACTIVE': {
                                setCurrentStatus('REGISTERED');
                                setTokenizationError(undefined);
                                break;
                            }

                            case 'NEEDS_IDENTITY_VERIFICATION': {
                                setCurrentStatus('UNREGISTERED');
                                setTokenizationError(undefined);
                                break;
                            }

                            default:
                                break;
                        }

                        break;
                    }

                    case 'GTP_TOKEN_STATUS_ERROR': {
                        switch (data.payload) {
                            case 'TOKEN_STATUS_CHECK_FAILED':

                            // eslint-disable-next-line no-fallthrough
                            case 'TOKENIZATION_CHECK_FAILED': {
                                setTokenizationError(text.googleWalletGetGoingTokenizationErrorOperation);
                                break;
                            }

                            case 'DEFAULT_WALLET_CHECK_FAILED': {
                                // google wallet not default --> make it default
                                setTokenizationError(text.googleWalletGetGoingTokenizationErrorNotDefault);
                                break;
                            }

                            case 'NO_ACTIVE_WALLET': {
                                // google wallet not activated
                                setTokenizationError(text.googleWalletGetGoingTokenizationErrorNotActivatedOrInstalled);
                                break;
                            }

                            case 'TOKEN_NOT_FOUND': {
                                // Token reference ID not found in wallet
                                setCurrentStatus('UNREGISTERED');
                                break;
                            }

                            // token is invalid state token needs to be deleted before trying again
                            case 'INVALID_TOKEN_STATE':
                                setTokenizationError(text.googleWalletGetGoingTokenizationErrorOperation);

                            // eslint-disable-next-line no-fallthrough
                            case 'ATTESTATION_ERROR': {
                                // wallet not default
                                setTokenizationError(text.googleWalletGetGoingTokenizationErrorAlreadyExists);
                                break;
                            }

                            default:
                                break;
                        }

                        break;
                    }

                    case 'GTP_PUSH_PROVISION_RESULT': {
                        // TODO change the check and return from native
                        const gtpPushProvisionResult = data.payload as GtpPushProvisionResult;

                        if (gtpPushProvisionResult.success) {
                            // TODO: use gtpPushProvisionResult.tokenReferenceId for something?
                            setCurrentStatus('REGISTERED');
                            setTokenizationError(undefined);
                        } else {
                            setTokenizationError(text.googleWalletPushProvisionCancelled);
                            setCurrentStatus('UNREGISTERED');
                        }
                        queryClient.invalidateQueries({ queryKey: ['v4', 'wallet', 'credit-cards', 'eligible'] });

                        break;
                    }

                    case 'GTP_PUSH_PROVISION_ERROR': {
                        setTokenizationError(text.googleWalletPushProvisionError);
                        setCurrentStatus('UNREGISTERED');

                        queryClient.invalidateQueries({ queryKey: ['v4', 'wallet', 'credit-cards', 'eligible'] });

                        break;
                    }

                    default:
                        break;
                }
            }
        },
        [text, queryClient],
    );

    useNativeEventListener(gpayReturnHandle);

    return { currentStatus, tokenizationError };
};
